import * as React from "react";
import { Announced } from "office-ui-fabric-react/lib/Announced";
import { Web } from "@pnp/sp/webs";
import {
  DetailsList,
  DetailsListLayoutMode,
  IColumn,
  Selection,
  SelectionMode,
} from "office-ui-fabric-react/lib/DetailsList";
import { MarqueeSelection } from "office-ui-fabric-react/lib/MarqueeSelection";
import {
  mergeStyles,
  mergeStyleSets,
} from "office-ui-fabric-react/lib/Styling";
import "@pnp/sp/files";
import "@pnp/sp/folders";
import JSZip from "jszip";
import axios, {
  AxiosProgressEvent,
  AxiosRequestConfig,
  ResponseType,
} from "axios";
import { saveAs } from "file-saver";
import {
  ISearchBoxStyles,
  SearchBox,
} from "office-ui-fabric-react/lib/SearchBox";
import { Spinner, SpinnerSize } from "office-ui-fabric-react/lib/Spinner";
import { Overlay, ProgressIndicator } from "office-ui-fabric-react";
import { DateHelper } from "./DateHelper";
import { IPdmsUser } from "./IPdmsUser";
import { downloadQueueStore } from "./downloadQueueStore";

const LARGE_FILE_THRESHOLD_BYTES = 50 * 1024 * 1024; // 50MB

const searchWrapperClass = mergeStyles({
  float: "right",
  margin: "10px",
  display: "block",
});

const tabTableWrapper = mergeStyles({
  width: "100%",
  overflowY: "scroll",
  overflowX: "scroll",
});

const detailsListWrapper = mergeStyles({
  width: "100% !important",
  margin: "0px",
  overflowY: "auto",
});

const classNames = mergeStyleSets({
  fileIconHeaderIcon: {
    padding: 0,
    fontSize: "16px",
  },
  fileIconCell: {
    textAlign: "center",
    selectors: {
      "&:before": {
        content: ".",
        display: "inline-block",
        verticalAlign: "middle",
        height: "100%",
        width: "0px",
        visibility: "hidden",
      },
    },
  },
  fileIconImg: {
    verticalAlign: "middle",
    maxHeight: "16px",
    maxWidth: "16px",
  },
  controlWrapper: {
    display: "flex",
    flexWrap: "wrap",
  },
  exampleToggle: {
    display: "inline-block",
    marginBottom: "10px",
    marginRight: "30px",
  },
  selectionDetails: {
    marginBottom: "20px",
  },
});

const searchBoxStyles: Partial<ISearchBoxStyles> = { root: { width: 200 } };

export interface IDetailsListDocumentsState {
  columns: IColumn[];
  items: IDocument[];
  selectionDetails: string;
  isModalSelection: boolean;
  isCompactMode: boolean;
  announcedMessage?: string;
  jsonResponse?: any;
  deleting: boolean;
  adding: boolean;
  downloading: boolean;
  targetListRefresh: boolean;
  isBusy?: boolean;
  progressPercent: number;
  progressLabel: string;
  progressDescription: string;
  showProgress: boolean;
}

export interface IDetailsListDocumentsProps {
  currentSite: string;
  currentProject: string;
  deleted: boolean;
  added: boolean;
  downloaded: boolean;
  handlerDeleted: any;
  handlerAdded: any;
  handlerDownload: any;
  currentUserInProject?: IPdmsUser;
}

export interface IDocument {
  key: string;
  name: string;
  value: string;
  iconName: string;
  fileType: string;
  fileSize: string;
  modifiedBy: string;
  dateModified: string;
  dateModifiedValue: number;
  fileSizeValue: number;
  fileSizeBytes: number;
}

var docsNonContorlBeforeSeach: any[] = [];
export class NonControlDocuments extends React.Component<
  IDetailsListDocumentsProps,
  IDetailsListDocumentsState
> {
  private _selection: Selection;
  private _allItems: IDocument[];

  componentDidMount() {
    if (this.props.currentUserInProject !== undefined) {
      this.loadDataWithCallback();
    }
  }

  constructor(props: IDetailsListDocumentsProps) {
    super(props);
    this._allItems = [];

    const columns: IColumn[] = [
      {
        key: "column1",
        name: "File Type",
        className: classNames.fileIconCell,
        iconClassName: classNames.fileIconHeaderIcon,
        ariaLabel:
          "Column operations for File type, Press to sort on File type",
        iconName: "Page",
        isIconOnly: true,
        fieldName: "name",
        minWidth: 16,
        maxWidth: 16,
        onColumnClick: this._onColumnClick,
        onRender: (item: IDocument) => {
          return (
            <img
              src={item.iconName}
              className={classNames.fileIconImg}
              alt={item.fileType + " file icon"}
            />
          );
        },
      },
      {
        key: "column0",
        name: "File Size",
        fieldName: "fileSizeValue",
        minWidth: 210,
        maxWidth: 350,
        isRowHeader: true,
        isResizable: true,
        isSorted: true,
        isSortedDescending: false,
        onColumnClick: this._onColumnClick,
        data: "number",
        onRender: (item: IDocument) => {
          return <span>{item.fileSize}</span>;
        },
        isPadded: true,
      },
      {
        key: "column2",
        name: "Name",
        fieldName: "name",
        minWidth: 210,
        maxWidth: 350,
        isRowHeader: true,
        isResizable: true,
        isSorted: true,
        isSortedDescending: false,
        sortAscendingAriaLabel: "Sorted A to Z",
        sortDescendingAriaLabel: "Sorted Z to A",
        onColumnClick: this._onColumnClick,
        data: "string",
        isPadded: true,
      },
      {
        key: "column3",
        name: "Date Modified",
        fieldName: "dateModifiedValue",
        minWidth: 70,
        maxWidth: 90,
        isResizable: true,
        onColumnClick: this._onColumnClick,
        data: "number",
        onRender: (item: IDocument) => {
          return <span>{item.dateModified}</span>;
        },
        isPadded: true,
      },
      {
        key: "column4",
        name: "Modified By",
        fieldName: "modifiedBy",
        minWidth: 70,
        maxWidth: 90,
        isResizable: true,
        isCollapsible: true,
        data: "string",
        onColumnClick: this._onColumnClick,
        onRender: (item: IDocument) => {
          return <span>{item.modifiedBy}</span>;
        },
        isPadded: true,
      },
    ];

    this._selection = new Selection({
      onSelectionChanged: () => {
        let element: HTMLElement = document.getElementById(
          "btnPanelNonControl",
        ) as HTMLElement;
        if (this._selection.getSelection().length > 0) {
          element.textContent = (
            this._selection.getSelection()[0] as IDocument
          ).key;
          this.setState({
            selectionDetails: this._getSelectionDetails(),
          });
        }
      },
    });

    this.state = {
      items: this._allItems,
      columns: columns,
      selectionDetails: this._getSelectionDetails(),
      isModalSelection: true,
      isCompactMode: false,
      announcedMessage: undefined,
      deleting: false,
      adding: false,
      targetListRefresh: false,
      downloading: false,
      isBusy: false,
      showProgress: false,
      progressPercent: 0,
      progressLabel: "File download progress",
      progressDescription: "",
    };

    this.nonCtrAddHandler = this.nonCtrAddHandler.bind(this);
  }

  public render() {
    const {
      columns,
      isCompactMode,
      items,
      selectionDetails,
      isModalSelection,
      announcedMessage,
    } = this.state;

    return (
      <div className={tabTableWrapper}>
        <table className={detailsListWrapper}>
          <tbody>
            <tr>
              <td>
                <div className={searchWrapperClass}>
                  <SearchBox
                    styles={searchBoxStyles}
                    placeholder="Search"
                    onClear={(ev) => {
                      this.setState({ items: docsNonContorlBeforeSeach });
                    }}
                    onSearch={(_sKey) => this._searchApply(_sKey)}
                  />
                </div>
              </td>
            </tr>
            <tr>
              <td>
                <Announced
                  message={`Number of items after filter applied: ${items.length} in ${this.props.currentProject}.`}
                />
              </td>
            </tr>
            {/* </div> */}
            <tr>
              <td colSpan={2}>
                <div className={classNames.selectionDetails}>
                  {selectionDetails}
                  <Announced message={selectionDetails} />
                  {announcedMessage ? (
                    <Announced message={announcedMessage} />
                  ) : undefined}
                </div>
              </td>
            </tr>
            <tr>
              {this.state.showProgress ? (
                <td width={100}>
                  <ProgressIndicator
                    label={this.state.progressLabel}
                    description={this.state.progressDescription}
                    percentComplete={this.state.progressPercent}
                    barHeight={5}
                  />
                </td>
              ) : null}

              <td colSpan={2}>
                {this.state.isBusy ? (
                  <Overlay>
                    <Spinner size={SpinnerSize.large}></Spinner>
                  </Overlay>
                ) : null}
              </td>
            </tr>
            <tr>
              <td colSpan={2}>
                {isModalSelection ? (
                  <MarqueeSelection selection={this._selection}>
                    <DetailsList
                      items={items}
                      compact={isCompactMode}
                      columns={columns}
                      selectionMode={SelectionMode.multiple}
                      getKey={this._getKey}
                      setKey="multiple"
                      layoutMode={DetailsListLayoutMode.justified}
                      isHeaderVisible={true}
                      selection={this._selection}
                      selectionPreservedOnEmptyClick={true}
                      onItemInvoked={this._onItemInvoked}
                      enterModalSelectionOnTouch={true}
                      ariaLabelForSelectionColumn="Toggle selection"
                      ariaLabelForSelectAllCheckbox="Toggle selection for all items"
                      checkButtonAriaLabel="Row checkbox"
                    />
                  </MarqueeSelection>
                ) : (
                  <DetailsList
                    items={items}
                    compact={isCompactMode}
                    columns={columns}
                    selectionMode={SelectionMode.none}
                    getKey={this._getKey}
                    setKey="none"
                    layoutMode={DetailsListLayoutMode.justified}
                    isHeaderVisible={true}
                    onItemInvoked={this._onItemInvoked}
                  />
                )}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    );
  }

  private loadDataWithCallback(mode: string = "") {
    if (
      this.props.currentProject === null ||
      this.props.currentProject === undefined ||
      this.props.currentUserInProject === undefined
    )
      return;

    if (this.state.isBusy === false) {
      this.setState({
        isBusy: true,
      });
    }
    axios
      .get(
        `/api/v1/noneCtrDocs/list?projectNo=${this.props.currentProject}&userId=${this.props.currentUserInProject?.key}`,
      )
      .then((response) => {
        let _allItems: any[] = [];

        response.data.body.forEach(
          (item: {
            ID: { toString: () => any };
            File: { Name: any; Length: any } | null;
            Document_x0020_Type: string | null | undefined;
            ModifiedBy: any;
            Modified: any;
          }) => {
            _allItems.push({
              key: item.ID.toString(),
              name: item.File === null ? "" : item.File.Name,
              value: item.ID,
              iconName:
                item.File === null
                  ? ""
                  : _randomFileIcon(
                      item.File.Name.split(".").pop().toUpperCase(),
                    ).url,
              fileType:
                item.File === null
                  ? ""
                  : item.File.Name.split(".").pop().toUpperCase(),
              modifiedBy:
                item.ModifiedBy === undefined ? "" : item.ModifiedBy.Title,
              fileSize:
                item == null
                  ? "0 kb"
                  : `${Math.round(item.File?.Length / 1024)} kb`,
              dateModified: DateHelper.FormatDateToLongDate(item.Modified),
              dateModifiedValue: item.Modified.valueOf(),
              fileSizeValue: (item.File?.Length / 10240).valueOf(),
              fileSizeBytes: item.File?.Length || 0,
            });
          },
        );
        this.setState({
          items: _allItems,
          isBusy: false,
        });
        docsNonContorlBeforeSeach = _allItems;
        if (mode === "deleteing") {
          this.props.handlerDeleted();
        }
        if (mode === "adding") {
          this.props.handlerAdded();
        }
      });
  }

  private nonCtrAddHandler(): void {
    this.setState({
      targetListRefresh: true,
    });
  }

  public componentDidUpdate(
    previousProps: IDetailsListDocumentsProps,
    previousState: IDetailsListDocumentsState,
  ) {
    if (
      previousState.isModalSelection !== this.state.isModalSelection &&
      !this.state.isModalSelection
    ) {
      this._selection.setAllSelected(false);
    }
    //refresh when change project
    if (previousProps.currentProject !== this.props.currentProject) {
      //alert(previousProps.currentProject);
      this.loadDataWithCallback();
    }
    if (
      previousProps.currentUserInProject !== this.props.currentUserInProject
    ) {
      //alert(previousProps.currentProject);
      this.loadDataWithCallback();
    }

    //refresh when change deleted property
    if (previousProps.deleted === true && this.props.deleted === false) {
      //alert(previousProps.currentProject);
      this.loadDataWithCallback();
    }

    //refresh when change added property
    if (previousProps.added === true && this.props.added === false) {
      this.loadDataWithCallback();
    }

    if (previousProps.deleted === false && this.props.deleted === true) {
      //alert('call delete function here');
      if (this._selection.getSelection().length > 0) {
        let id = (this._selection.getSelection()[0] as IDocument).key;
        var y: number = +id;
        let request = {
          projectNo: this.props.currentProject,
          fileId: y,
        };
        axios.post("/api/v1/fileDelete", request).then((res) => {
          this.props.handlerDeleted();
        });
      } else {
        this.props.handlerDeleted();
      }
    }

    if (previousProps.added === false && this.props.added === true) {
      this.props.handlerAdded();
      //this.loadDataWithCallback("adding");
    }
    if (previousProps.downloaded === false && this.props.downloaded === true) {
      const selection = this._selection.getSelection() as IDocument[];
      if (selection.length === 0) {
        alert("Please select your items");
      } else if (
        selection.some((it) => it.fileSizeBytes > LARGE_FILE_THRESHOLD_BYTES)
      ) {
        this._downloadViaQueue(selection);
      } else if (selection.length === 1) {
        this._downloadSelectedItems();
      } else {
        this._downloadAndZipSelectedItems();
      }
    }
  }

  private _downloadSelectedItems(): void {
    if (this._selection.getSelection().length > 0) {
      this.setState({
        isBusy: true,
        showProgress: true,
      });

      const config: AxiosRequestConfig = {
        responseType: "blob" as ResponseType,
        onDownloadProgress: (progressEvent: AxiosProgressEvent) => {
          if (progressEvent.total) {
            const percent = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total,
            );
            this.setState({ progressPercent: percent });
          } else {
            this.setState({ progressPercent: 0 });
          }
        },
      };

      let fileName = (this._selection.getSelection()[0] as IDocument).name;

      axios
        .get(
          `/api/v1/singleFileDownload?libraryName=NonProject%20DOcuments&fileName=${fileName}&projectNo=${this.props.currentProject}`,
          config,
        )
        .then((res) => {
          const url = window.URL.createObjectURL(new Blob([res.data]));
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute("download", `${fileName}`);
          document.body.appendChild(link);
          link.click();
          this.setState({
            isBusy: false,
            showProgress: false,
          });
          this.props.handlerDownload();
        });
    }
  }

  // large files (>50MB) skip the inline spinner/progress-indicator and are
  // handed off to downloadQueueStore, which drives the floating DownloadManager
  // widget (bottom-right) instead - each selected file is shown and downloads
  // separately in the widget; once all of them finish, they're bundled into
  // a single zip and saved (single selections just save as themselves)
  private _downloadViaQueue(selection: IDocument[]): void {
    if (selection.length === 1) {
      const item = selection[0];
      downloadQueueStore
        .download(
          `/api/v1/singleFileDownload?libraryName=NonProject%20DOcuments&fileName=${item.name}&projectNo=${this.props.currentProject}`,
          item.name,
          item.fileSizeBytes,
        )
        .catch(() => {
          // errors are surfaced in the DownloadManager widget itself
        });
    } else {
      const zipFilename = `${this.props.currentProject}_NonCtrlDocs.zip`;
      downloadQueueStore
        .downloadAllThenZip(
          selection.map((item) => ({
            url: `/api/v1/singleFileDownload?libraryName=NonProject%20DOcuments&fileName=${item.name}&projectNo=${this.props.currentProject}`,
            fileName: item.name,
            sizeBytes: item.fileSizeBytes,
          })),
          zipFilename,
        )
        .catch(() => {
          // errors are surfaced in the DownloadManager widget itself
        });
    }
    this.props.handlerDownload();
  }

  private _downloadAndZipSelectedItems_back(): void {
    var zipFilename = `${this.props.currentProject}_NonContrlDocs.zip`;
    var zip = new JSZip();
    var allPromise = [];
    if (this._selection.getSelection().length > 0) {
      this.setState({
        isBusy: true,
      });
      for (let i = 0; i < this._selection.getSelection().length; i++) {
        let fileName = (this._selection.getSelection()[i] as IDocument).name;
        let c_promise = new Promise((resolve, reject) => {});
        allPromise.push(c_promise);
        axios
          .get(
            `/api/v1/singleFileDownload?libraryName=NonProject%20DOcuments&fileName=${fileName}&projectNo=${this.props.currentProject}`,
            { responseType: "blob" },
          )
          .then((res) => {
            zip.file(fileName, res.data, { binary: true });
            if (i + 1 === this._selection.getSelection().length) {
              zip
                .generateAsync({ type: "blob" })
                .then(function (content) {
                  saveAs(content, zipFilename);
                })
                .then(() => {
                  this.setState({
                    isBusy: false,
                  });
                  this.props.handlerDownload();
                });
            }
          });
      }
    }
  }

  private _downloadAndZipSelectedItems(): void {
    const zipFilename = `${this.props.currentProject}_NonContrlDocs.zip`;
    const zip = new JSZip();
    const selections = this._selection.getSelection();

    if (selections.length > 0) {
      this.setState({ isBusy: true });

      const downloadPromises = selections.map((item: any) => {
        const fileName = item.name;

        return axios
          .get(
            `/api/v1/singleFileDownload?libraryName=NonProject%20DOcuments&fileName=${fileName}&projectNo=${this.props.currentProject}`,
            { responseType: "blob" },
          )
          .then((res) => {
            zip.file(fileName, res.data, { binary: true });
          });
      });

      Promise.all(downloadPromises)
        .then(() => zip.generateAsync({ type: "blob" }))
        .then((content) => {
          saveAs(content, zipFilename);
          this.setState({ isBusy: false });
          this.props.handlerDownload();
        })
        .catch((error) => {
          console.error("Error downloading or zipping files:", error);
          this.setState({ isBusy: false });
        });
    }
  }

  private _getKey(item: any, index?: number): string {
    return item.key;
  }

  private _searchApply(keystring: string): void {
    this.setState({
      items: keystring
        ? this.state.items.filter(
            (i) => i.name.toLowerCase().indexOf(keystring.toLowerCase()) > -1,
          )
        : this.state.items,
    });
  }

  private _onChangeCompactMode = (
    ev: React.MouseEvent<HTMLElement>,
    checked: boolean,
  ): void => {
    this.setState({ isCompactMode: checked });
  };

  private _onChangeModalSelection = (
    ev: React.MouseEvent<HTMLElement>,
    checked: boolean,
  ): void => {
    this.setState({ isModalSelection: checked });
  };

  private _onChangeText = (
    ev: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>,
    text: string,
  ): void => {
    this.setState({
      items: text
        ? this._allItems.filter((i) => i.name.toLowerCase().indexOf(text) > -1)
        : this._allItems,
    });
  };

  private _onItemInvoked(item: any): void {
    alert(`Item invoked: ${item.name}`);
  }

  private _getSelectionDetails(): string {
    const selectionCount = this._selection.getSelectedCount();

    switch (selectionCount) {
      case 0:
        return "No items selected";
      case 1:
        return (
          "1 item selected: " +
          (this._selection.getSelection()[0] as IDocument).name
        );
      default:
        return `${selectionCount} items selected`;
    }
  }

  private _onColumnClick = (
    ev: React.MouseEvent<HTMLElement>,
    column: IColumn,
  ): void => {
    const { columns, items } = this.state;
    const newColumns: IColumn[] = columns.slice();
    const currColumn: IColumn = newColumns.filter(
      (currCol) => column.key === currCol.key,
    )[0];
    newColumns.forEach((newCol: IColumn) => {
      if (newCol === currColumn) {
        currColumn.isSortedDescending = !currColumn.isSortedDescending;
        currColumn.isSorted = true;
        this.setState({
          announcedMessage: `${currColumn.name} is sorted ${
            currColumn.isSortedDescending ? "descending" : "ascending"
          }`,
        });
      } else {
        newCol.isSorted = false;
        newCol.isSortedDescending = true;
      }
    });
    const newItems = _copyAndSort(
      items,
      currColumn.fieldName!,
      currColumn.isSortedDescending,
    );
    this.setState({
      columns: newColumns,
      items: newItems,
    });
  };

  private async getNonControlDocuments() {
    //remove hardcode later
    let web = Web(
      "https://apyokogawa.sharepoint.com/sites/EDMSV1/20191002EDMS116",
    );
    //let list = await web.lists.getByTitle("NonProject Documents").items.select("Title", "Lookup/Title", "Lookup/ID").expand("Lookup").get();
    let list = await web.lists
      .getByTitle("NonProject Documents")
      .items.select(
        "Id",
        "Title",
        "Document_x0020_Category",
        "Modified",
        "Editor",
        "File",
      )
      .expand("Editor", "File")
      .get(); //.filter(`ModifiedBy/EmpNo eq 'remala.abhilash'`)
    this.setState({ jsonResponse: list });
  }
}

function _copyAndSort<T>(
  items: T[],
  columnKey: string,
  isSortedDescending?: boolean,
): T[] {
  const key = columnKey as keyof T;
  return items
    .slice(0)
    .sort((a: T, b: T) =>
      (isSortedDescending ? a[key] < b[key] : a[key] > b[key]) ? 1 : -1,
    );
}

function _randomFileIcon(docType: string = "docx"): {
  docType: string;
  url: string;
} {
  return {
    docType,
    url: `https://static2.sharepointonline.com/files/fabric/assets/item-types/16/${docType.toLowerCase()}.svg`,
  };
}
